/**
 * Өдрийн борлуулалт — кассын өдрийн тайлангийн бүтэцтэй ижил
 * (`summarizeDay`), гэхдээ БҮХ салбарын нийлбэр:
 *
 *   нийт үнэ − хөнгөлөлт = цэвэр борлуулалт
 *   + Зээл, И-баримтын нэмэгдэл = НИЙТ АВСАН = төлбөрийн хэлбэрүүдийн нийлбэр
 *   Хувь зээлтэй өдөр: НИЙТ ДҮН − хувь зээл (авлага) = НИЙТ АВСАН
 *   (кассын цаас `dayCloseText`-тэй ЯГ ижил нэр, дараалал)
 *
 * Хүчингүй баримт, буцаалт нийлбэрт орохгүй — тусдаа мөр (цаасан дээр ч
 * «Буцаалт (N) −X» мөр бий). Гутал бүрийн буцаалт `DayStock`-т.
 *
 * ⚠️ «Орлого» гэж нэрлэхгүй: Тайлангийн «Орлого» нь хожим буцаагдсаныг
 *    хассан, энэ нь кассын өдрийн тоо.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  LEGACY_CREDIT_FEE_LABEL, VAT_PCT, formatMnt, formatQty, formatTime, type CalendarDayReport,
} from '@boots/domain';
import { useAuth } from '../../lib/auth';
import { DayBlock } from './day-block';
import { DataTable, type Column } from '../data-table';

type Order = CalendarDayReport['orders'][number];

export function DaySales({ r }: { r: CalendarDayReport }) {
  const s = r.sales;
  if (r.orders.length === 0 && s.returns.count === 0) return null;
  const installment = Number(s.installmentMnt) > 0;
  // Солилцоонд буцаасан гутлын үнээр тооцогдсон хэсэг — мөнгө биш (2026-10-03)
  const exchange = Number(s.exchangeMnt) > 0;
  const deducted = installment || exchange;

  return (
    <>
      <DayBlock title="Борлуулалт">
        <div className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <span className="text-label text-ink-3 uppercase">Борлуулалтын задаргаа</span>
            <Line label="Нийт үнэ" value={formatMnt(s.grossMnt)} />
            {Number(s.discountMnt) > 0 ? <Line label="Хөнгөлөлт" value={`-${formatMnt(s.discountMnt)}`} /> : null}
            <Line label="Цэвэр борлуулалт" value={formatMnt(s.salesMnt)} strong />
            {Number(s.creditFeeMnt) > 0 ? (
              <Line label={LEGACY_CREDIT_FEE_LABEL} value={formatMnt(s.creditFeeMnt)} muted />
            ) : null}
            {Number(s.vatMnt) > 0 ? (
              <Line label={`И-баримт +${VAT_PCT}%`} value={formatMnt(s.vatMnt)} muted />
            ) : null}
            {/*
              ⚠️ Кассын цаастай ИЖИЛ (`dayCloseText`): хувь зээлийн 90% нь
                 авлага, солилцооны хэсэг нь буцаасан гутал — хоёулаа касст
                 ороогүй тул «Нийт авсан»-аас хасагдана.
            */}
            <div className="mt-1 border-t border-hair pt-2">
              <Line label={deducted ? 'Нийт дүн' : 'Нийт авсан'} value={formatMnt(s.totalMnt)} strong />
            </div>
            {installment ? <Line label="Хувь зээл (авлага)" value={`-${formatMnt(s.installmentMnt)}`} muted /> : null}
            {exchange ? <Line label="Солилцоо (буцаасан гутлаар)" value={`-${formatMnt(s.exchangeMnt)}`} muted /> : null}
            {deducted ? <Line label="Нийт авсан" value={formatMnt(s.collectedMnt)} strong /> : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-label text-ink-3 uppercase">Төлбөрийн хэлбэр</span>
            {s.payments.length === 0 ? <span className="text-meta text-ink-3">—</span> : null}
            {s.payments.map((p) => (
              <Line key={p.label} label={`${p.label} (${formatQty(p.count)})`} value={formatMnt(p.amountMnt)} />
            ))}
          </div>
        </div>

        {/* Өдрийн буцаалтын НИЙЛБЭР зөвхөн энд — доорх «Буцаалт» нь гутал бүрээр */}
        {s.voided.count > 0 || s.returns.count > 0 ? (
          <div className="flex flex-col gap-1 rounded-well bg-well px-3 py-2 text-meta text-ink-2">
            {s.voided.count > 0 ? (
              <span>
                Хүчингүй болгосон {formatQty(s.voided.count)} баримт · {formatMnt(s.voided.totalMnt)} — нийлбэрт ороогүй
              </span>
            ) : null}
            {s.returns.count > 0 ? (
              <span className="text-alert-ink">
                Буцаалт {formatQty(s.returns.count)} гутал · -{formatMnt(s.returns.refundMnt)} буцааж өгсөн
                {s.returns.byMethod.length > 0
                  ? ` (${s.returns.byMethod.map((m) => `${m.label} ${formatMnt(m.amountMnt)}`).join(', ')})`
                  : ''}
              </span>
            ) : null}
          </div>
        ) : null}
      </DayBlock>

      {r.stores.length > 0 ? (
        <DayBlock title="Салбар ба худалдагч">
          {/* Хоёр багана ӨӨР суурьтай — худалдагчийнх нэмэгдэлтэй дүн */}
          <div className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <span className="text-label text-ink-3 uppercase">Салбараар · цэвэр борлуулалт</span>
              {r.stores.map((st) => (
                <Line
                  key={st.storeId}
                  label={
                    <>
                      {st.name}
                      <span className="ml-1.5 text-meta text-ink-3">
                        {formatQty(st.orders)} баримт · {formatQty(st.units)} ш
                      </span>
                    </>
                  }
                  value={formatMnt(st.salesMnt)}
                />
              ))}
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-label text-ink-3 uppercase">Худалдагчаар · нийт дүн</span>
              {s.sellers.map((p) => (
                <Line
                  key={p.name}
                  label={
                    <>
                      {p.name}
                      <span className="ml-1.5 text-meta text-ink-3">{formatQty(p.orders)} баримт</span>
                    </>
                  }
                  value={formatMnt(p.totalMnt)}
                  muted
                />
              ))}
            </div>
          </div>
        </DayBlock>
      ) : null}

      {r.orders.length > 0 ? (
        <DayBlock title="Баримтууд" count={r.orders.length}>
          <OrdersTable orders={r.orders} />
        </DayBlock>
      ) : null}
    </>
  );
}

function Line({ label, value, strong = false, muted = false }: {
  label: ReactNode;
  value: string;
  strong?: boolean;
  muted?: boolean;
}) {
  return (
    <div className={`flex items-baseline gap-3 text-body ${strong ? 'font-semibold' : ''} ${muted ? 'text-ink-2' : ''}`}>
      <span className="min-w-0">{label}</span>
      <span className="num ml-auto whitespace-nowrap">{value}</span>
    </div>
  );
}

function OrdersTable({ orders }: { orders: Order[] }) {
  const { hasPerm } = useAuth();
  const canOpen = hasPerm('orders.view');

  const columns: Column<Order>[] = [
    { key: 'time', header: 'Цаг', nowrap: true, cell: (o) => formatTime(o.soldAt) },
    {
      key: 'code',
      header: 'Баримт',
      mobile: 'title',
      nowrap: true,
      cell: (o) => (
        <span className="inline-flex items-center gap-1.5">
          {canOpen ? (
            <Link to={`/orders?open=${o.id}`} className="num text-accent-700 hover:underline">{o.code}</Link>
          ) : (
            <span className="num">{o.code}</span>
          )}
          {o.status === 'VOIDED' ? (
            <span className="rounded-pill bg-alert-bg px-2 text-micro text-alert-ink">Хүчингүй</span>
          ) : null}
        </span>
      ),
    },
    { key: 'store', header: 'Салбар', cell: (o) => o.storeName },
    { key: 'seller', header: 'Худалдагч', cell: (o) => o.sellerName },
    { key: 'units', header: 'Ширхэг', align: 'right', cell: (o) => `${formatQty(o.units)} ш` },
    { key: 'payment', header: 'Төлбөр', cell: (o) => o.paymentLabel },
    { key: 'total', header: 'Дүн', align: 'right', mobile: 'value', cell: (o) => formatMnt(o.totalMnt) },
  ];

  return (
    <DataTable
      rows={orders}
      columns={columns}
      rowKey={(o) => o.id}
      rowClass={(o) => (o.status === 'VOIDED' ? 'opacity-55' : '')}
    />
  );
}
